import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import {type AppBskyFeedDefs} from '@atproto/api'

import {
  wordGroupsApiClient,
  type WordGroup,
  type WordGroupBio,
  type WordGroupVideosResponse,
  type PublicFeed,
  type PublicFeedVideosResponse,
} from '#/lib/api/word-groups'
import {useAgent} from '#/state/session'
import {STALE} from '.'

// Enriched response type where posts have been replaced with full Bluesky PostView objects
export interface EnrichedFeedVideosResponse {
  posts: AppBskyFeedDefs.PostView[]
  total: number
  hasMore: boolean
  limit: number
  offset: number
  feed: PublicFeed
}

const WORD_GROUPS_QUERY_KEY_ROOT = 'word-groups'
const WORD_GROUP_BIOS_QUERY_KEY_ROOT = 'word-group-bios'
const WORD_GROUP_VIDEOS_QUERY_KEY_ROOT = 'word-group-videos'
const PUBLIC_FEEDS_QUERY_KEY_ROOT = 'public-feeds'
const PUBLIC_FEED_VIDEOS_QUERY_KEY_ROOT = 'public-feed-videos'

export interface WordGroupBiosResponse {
  bios: WordGroupBio[]
  total: number
  wordGroup: WordGroup
}

/**
 * Fetch all word groups (public endpoint)
 */
export function useWordGroupsQuery(opts?: {enabled?: boolean}) {
  return useQuery<WordGroup[], Error>({
    enabled: opts?.enabled !== false,
    staleTime: STALE.MINUTES.FIVE,
    queryKey: [WORD_GROUPS_QUERY_KEY_ROOT],
    queryFn: async () => {
      return wordGroupsApiClient.getWordGroups()
    },
  })
}

/**
 * Fetch users whose bios match a specific word group.
 * Returns lightweight bio data directly from the API without
 * requiring a Bluesky API re-fetch. Full profiles are fetched
 * on-demand when needed (e.g., when following a user).
 */
export function useWordGroupBiosQuery(
  wordGroupId: number | null,
  opts?: {enabled?: boolean; limit?: number},
) {
  return useQuery<WordGroupBiosResponse, Error>({
    enabled: opts?.enabled !== false && wordGroupId !== null,
    staleTime: STALE.MINUTES.THREE,
    queryKey: [WORD_GROUP_BIOS_QUERY_KEY_ROOT, wordGroupId],
    queryFn: async () => {
      if (wordGroupId === null) {
        throw new Error('No word group selected')
      }

      const biosResponse = await wordGroupsApiClient.getWordGroupBios({
        id: wordGroupId,
        limit: opts?.limit ?? 50,
      })

      return {
        bios: biosResponse.bios,
        total: biosResponse.total,
        wordGroup: biosResponse.wordGroup,
      }
    },
  })
}

/**
 * Fetch video posts from users in a specific word group.
 * Uses infinite query for pagination support.
 * Enriches posts with complete Bluesky data for video playback.
 */
export function useWordGroupVideosQuery(
  wordGroupId: number | null,
  opts?: {enabled?: boolean},
) {
  const agent = useAgent()

  return useInfiniteQuery<
    WordGroupVideosResponse,
    Error,
    InfiniteData<WordGroupVideosResponse>,
    QueryKey,
    number
  >({
    enabled: opts?.enabled !== false && wordGroupId !== null,
    staleTime: STALE.MINUTES.THREE,
    queryKey: [WORD_GROUP_VIDEOS_QUERY_KEY_ROOT, wordGroupId],
    queryFn: async ({pageParam}) => {
      if (wordGroupId === null) {
        throw new Error('No word group selected')
      }

      const response = await wordGroupsApiClient.getWordGroupVideos({
        id: wordGroupId,
        limit: 50,
        offset: pageParam,
      })

      // Fetch complete post data from Bluesky for video playlist URLs and engagement metrics
      if (response.posts && response.posts.length > 0 && agent) {
        try {
          const postUris = response.posts.map((post: any) => post.uri)

          // Fetch posts in batches of 25 (API limit)
          const batchSize = 25
          const postBatches = []
          for (let i = 0; i < postUris.length; i += batchSize) {
            const batch = postUris.slice(i, i + batchSize)
            postBatches.push(agent.getPosts({uris: batch}))
          }

          const batchResults = await Promise.all(postBatches)
          const blueskyPosts = batchResults.flatMap(r => r.data.posts)

          // Create a map of URI to complete post data
          const blueskyPostMap = new Map(blueskyPosts.map(p => [p.uri, p]))

          // Update posts with complete data from Bluesky
          for (let i = 0; i < response.posts.length; i++) {
            const tapPost = response.posts[i]
            const blueskyPost = blueskyPostMap.get(tapPost.uri)

            if (blueskyPost) {
              // Merge: Use Bluesky's complete data, preserve TAP extensions
              response.posts[i] = {
                ...blueskyPost,
                hasImages: tapPost.hasImages,
                hasVideo: tapPost.hasVideo,
                hasGif: tapPost.hasGif,
                imageCount: tapPost.imageCount,
                tags: tapPost.tags,
                tagsComplete: tapPost.tagsComplete,
              }
            }
          }
        } catch (error) {
          console.error(
            '[useWordGroupVideosQuery] Failed to fetch complete post data:',
            error,
          )
        }
      }

      return response
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) return undefined
      return allPages.length * 50
    },
  })
}

// Public Feeds API queries

/**
 * Fetch all public feeds
 */
export function useFeedsQuery(opts?: {enabled?: boolean}) {
  return useQuery<PublicFeed[], Error>({
    enabled: opts?.enabled !== false,
    staleTime: STALE.MINUTES.FIVE,
    queryKey: [PUBLIC_FEEDS_QUERY_KEY_ROOT],
    queryFn: async () => {
      return wordGroupsApiClient.getPublicFeeds()
    },
  })
}

/**
 * Fetch video posts from a public feed.
 * Uses infinite query for pagination support.
 * Enriches posts with complete Bluesky data for video playback.
 */
export function useFeedVideosQuery(
  feedSlug: string | null,
  opts?: {enabled?: boolean},
) {
  const agent = useAgent()

  return useInfiniteQuery<
    EnrichedFeedVideosResponse,
    Error,
    InfiniteData<EnrichedFeedVideosResponse>,
    QueryKey,
    number
  >({
    enabled: opts?.enabled !== false && feedSlug !== null,
    staleTime: STALE.MINUTES.THREE,
    queryKey: [PUBLIC_FEED_VIDEOS_QUERY_KEY_ROOT, feedSlug],
    queryFn: async ({pageParam}): Promise<EnrichedFeedVideosResponse> => {
      if (feedSlug === null) {
        throw new Error('No feed selected')
      }

      const response = await wordGroupsApiClient.getPublicFeedVideos({
        slug: feedSlug,
        limit: 50,
        offset: pageParam,
      })

      // Fetch complete post data from Bluesky for full PostView objects
      let enrichedPosts: AppBskyFeedDefs.PostView[] = []

      if (response.posts && response.posts.length > 0 && agent) {
        try {
          const postUris = response.posts.map(post => post.uri)

          // Fetch posts in batches of 25 (API limit)
          const batchSize = 25
          const postBatches = []
          for (let i = 0; i < postUris.length; i += batchSize) {
            const batch = postUris.slice(i, i + batchSize)
            postBatches.push(agent.getPosts({uris: batch}))
          }

          const batchResults = await Promise.all(postBatches)
          const blueskyPosts = batchResults.flatMap(r => r.data.posts)

          // Create a map of URI to complete post data
          const blueskyPostMap = new Map(blueskyPosts.map(p => [p.uri, p]))

          // Build enriched posts array preserving order from TAP API
          for (const tapPost of response.posts) {
            const blueskyPost = blueskyPostMap.get(tapPost.uri)
            if (blueskyPost) {
              enrichedPosts.push(blueskyPost)
            }
          }
        } catch (error) {
          console.error(
            '[useFeedVideosQuery] Failed to fetch complete post data:',
            error,
          )
        }
      }

      return {
        posts: enrichedPosts,
        total: response.total,
        hasMore: response.hasMore,
        limit: response.limit,
        offset: response.offset,
        feed: response.feed,
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) return undefined
      return allPages.length * 50
    },
  })
}
